// "Notify me when it's back" (product page): when the chosen color and size is sold out, the main Add to Bag
// and the sticky bar's button are swapped for a "Notify me when it's back" button. It opens a small form
// that signs the shopper up for Klaviyo's back in stock alert for that exact variant, through Klaviyo's
// client back in stock endpoint (the same alerts and "Back in stock" flow the old theme used). Ticking
// "Also send me news & offers" also adds them to the newsletter list. Styles: assets/sivana-notify.css.
const KLAVIYO_KEY = 'gxEyvt'; // Klaviyo public key (same as the Klaviyo app embed)
const NEWS_LIST = 'eswnGa'; // newsletter list the old theme's form offered
const REVISION = '2024-06-15';
const SOLD_OUT = /sold out/i;

const info = () => document.querySelector('.product-information');

function currentVariantId() {
  return info()?.querySelector('product-form-component input[name="id"]')?.value || '';
}

function makeButton() {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'sivana-notify-button';
  button.dataset.sivanaNotify = '';
  button.textContent = "Notify me when it's back";
  return button;
}

// The main button (inside add-to-cart-component) and the sticky bar's own button
function swapButtons() {
  const root = info();
  if (!root) return;
  const spots = [
    ...root.querySelectorAll('product-form-component add-to-cart-component'),
    ...document.querySelectorAll('sticky-add-to-cart .sticky-add-to-cart__button'),
  ];
  spots.forEach((spot) => {
    const button = spot.matches('button') ? spot : spot.querySelector('button');
    const soldOut = Boolean(button && button.disabled && SOLD_OUT.test(button.textContent));
    let notify = spot.nextElementSibling?.matches('[data-sivana-notify]') ? spot.nextElementSibling : null;
    if (soldOut && !notify) {
      notify = makeButton();
      spot.after(notify);
    }
    spot.classList.toggle('sivana-notify-swap', soldOut);
    if (notify) notify.hidden = !soldOut;
  });
}

let dialog;
function buildDialog() {
  dialog = document.createElement('dialog');
  dialog.className = 'sivana-notify';
  dialog.setAttribute('aria-labelledby', 'SivanaNotifyTitle');
  dialog.innerHTML = `
    <button type="button" class="sivana-notify__close" data-close aria-label="Close">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
    </button>
    <div class="sivana-notify__inner">
      <p class="sivana-notify__eyebrow">Back in stock alert</p>
      <h2 class="sivana-notify__title" id="SivanaNotifyTitle">Get notified</h2>
      <p class="sivana-notify__item"><span data-name></span><span data-variant></span></p>
      <form class="sivana-notify__form" novalidate>
        <p class="sivana-notify__text">Leave your email and we'll let you know as soon as it's back.</p>
        <label class="visually-hidden" for="SivanaNotifyEmail">Email address</label>
        <input class="sivana-notify__email" id="SivanaNotifyEmail" type="email" name="email" autocomplete="email" placeholder="Email address" required>
        <label class="sivana-notify__check"><input type="checkbox" name="news"><span>Also send me news &amp; offers</span></label>
        <button type="submit" class="sivana-notify__submit">Notify me</button>
        <p class="sivana-notify__error" role="alert" hidden></p>
      </form>
      <div class="sivana-notify__done" role="status" hidden>
        <p class="sivana-notify__done-title">You're on the list</p>
        <p class="sivana-notify__text" data-done></p>
        <button type="button" class="sivana-notify__submit sivana-notify__submit--outline" data-close>Keep shopping</button>
      </div>
    </div>`;
  document.body.append(dialog);
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target.closest('[data-close]')) dialog.close();
  });
  dialog.querySelector('form').addEventListener('submit', submit);
}

async function productDetails(variantId) {
  try {
    const path = window.location.pathname.replace(/\/$/, '');
    const product = await fetch(`${path}.js`).then((response) => response.json());
    const variant = product.variants.find((item) => String(item.id) === String(variantId));
    return {
      name: product.title.split(' -')[0].trim(),
      variant: variant?.public_title ? variant.public_title.replace(/\s*\/\s*/g, ' · ') : '',
    };
  } catch (error) {
    return { name: document.querySelector('.product-information h1')?.textContent.trim() || '', variant: '' };
  }
}

async function open() {
  if (!dialog) buildDialog();
  const variantId = currentVariantId();
  dialog.dataset.variantId = variantId;
  const form = dialog.querySelector('form');
  form.hidden = false;
  form.reset();
  dialog.querySelector('.sivana-notify__done').hidden = true;
  dialog.querySelector('.sivana-notify__error').hidden = true;
  dialog.querySelector('[data-name]').textContent = '';
  dialog.querySelector('[data-variant]').textContent = '';
  dialog.showModal();
  const details = await productDetails(variantId);
  dialog.querySelector('[data-name]').textContent = details.name;
  dialog.querySelector('[data-variant]').textContent = details.variant;
  dialog.dataset.variantLabel = details.variant;
}

function klaviyo(path, body) {
  return fetch(`https://a.klaviyo.com/client/${path}/?company_id=${KLAVIYO_KEY}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', revision: REVISION },
    body: JSON.stringify(body),
  });
}

async function submit(event) {
  event.preventDefault();
  const form = event.currentTarget;
  const email = form.email.value.trim();
  const error = form.querySelector('.sivana-notify__error');
  const button = form.querySelector('.sivana-notify__submit');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    error.textContent = 'Please enter a valid email address.';
    error.hidden = false;
    form.email.focus();
    return;
  }
  error.hidden = true;
  button.disabled = true;
  button.textContent = 'Signing you up…';
  const profile = { data: { type: 'profile', attributes: { email } } };
  try {
    const response = await klaviyo('back-in-stock-subscriptions', {
      data: {
        type: 'back-in-stock-subscription',
        attributes: { profile, channels: ['EMAIL'] },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${dialog.dataset.variantId}` } },
        },
      },
    });
    if (!response.ok) throw new Error(String(response.status));
    if (form.news.checked) {
      klaviyo('subscriptions', {
        data: {
          type: 'subscription',
          attributes: { profile, custom_source: 'Back in Stock' },
          relationships: { list: { data: { type: 'list', id: NEWS_LIST } } },
        },
      }).catch(() => {});
    }
    const label = dialog.dataset.variantLabel;
    dialog.querySelector('[data-done]').textContent = `We'll email ${email} as soon as ${label ? `${label} is` : "it's"} back.`;
    form.hidden = true;
    dialog.querySelector('.sivana-notify__done').hidden = false;
  } catch (problem) {
    error.textContent = "Something went wrong. Please try again in a moment.";
    error.hidden = false;
  } finally {
    button.disabled = false;
    button.textContent = 'Notify me';
  }
}

document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest('[data-sivana-notify]')) open();
});

// Horizon redraws the buy buttons when the color or size changes: keep the swap in step
if (info()) {
  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      swapButtons();
    }, 40);
  };
  new MutationObserver(queue).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  swapButtons();
}
